import { Extension } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion';

import { type WorkspaceAgentMentionItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { WORKSPACE_AGENT_MENTION_CHAR } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionConstants';

/** `@tiptap/suggestion` does not export its plugin state type; only `active` is read. */
export type WorkspaceAgentMentionSuggestionPluginState = {
  active: boolean;
};

/** Own key so the picker never shares plugin state with another suggestion. */
export const WORKSPACE_AGENT_MENTION_SUGGESTION_PLUGIN_KEY = new PluginKey<WorkspaceAgentMentionSuggestionPluginState>(
  'workspaceAgentMentionSuggestion',
);

export type WorkspaceAgentMentionSuggestionOptions = Omit<
  SuggestionOptions<WorkspaceAgentMentionItem, WorkspaceAgentMentionItem>,
  'editor'
>;

export type WorkspaceAgentMentionSuggestionExtensionOptions = {
  suggestion: WorkspaceAgentMentionSuggestionOptions;
};

/** Wraps `@tiptap/suggestion` directly, not `extension-mention`: a pick inserts the panel's own pill atom. */
export const WorkspaceAgentMentionSuggestion = Extension.create<WorkspaceAgentMentionSuggestionExtensionOptions>({
  addOptions() {
    return {
      suggestion: {
        allowedPrefixes: null,
        /**
         * A mentionable title is usually more than one word, so the query runs past a space and only
         * ends at the end of the text, at Escape, or at the next ` @`. Nothing else closes the picker
         * for the user, so `usePrivateWorkspaceAgentMentionSuggestionState` owns the auto-close — and
         * with it `shouldResetDismissed`, which has to tell that auto-close from Escape's.
         */
        allowSpaces: true,
        char: WORKSPACE_AGENT_MENTION_CHAR,
        pluginKey: WORKSPACE_AGENT_MENTION_SUGGESTION_PLUGIN_KEY,
        startOfLine: false,
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },

  name: 'workspaceAgentMentionSuggestion',
});
